import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Lock, CheckCircle2 } from "lucide-react";
import { AuthShell } from "./AuthPages";
import { useI18n } from "./i18n";
import { CL, pwValid } from "./api";

const inputCls = "w-full rounded-xl border border-[#dcdfd4] bg-white py-3 pl-10 pr-4 text-[14px] text-[#1b2420] outline-none transition-colors focus:border-[#14512f]";
const btnCls = "flex w-full items-center justify-center rounded-xl bg-[#14512f] px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#0f4526] disabled:opacity-50";

const IconInput = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a8f83]" />
    <input className={inputCls} {...props} />
  </div>
);

const BackToLogin = () => {
  const { t } = useI18n();
  return <Link to="/login" className="font-medium text-[#14512f] hover:underline" data-testid="back-to-login">{t("backToLogin")}</Link>;
};

export function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await CL.forgotPassword(email); } catch { /* generic response either way */ }
    setDone(true); setBusy(false);
  };
  return (
    <AuthShell title={t("forgotTitle")} subtitle={t("forgotDesc")} footer={<BackToLogin />}>
      {done ? (
        <div className="flex items-start gap-2 rounded-lg bg-[#eef6ef] px-4 py-3 text-sm text-[#14512f]" data-testid="forgot-done">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{t("forgotSent")}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3.5" data-testid="forgot-form">
          <IconInput icon={Mail} type="email" required placeholder={t("authEmail")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          <button type="submit" disabled={busy || !email} className={btnCls} data-testid="forgot-submit">{t("forgotSubmit")}</button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!pwValid(pw)) return setErr(t("pwRule"));
    if (pw !== pw2) return setErr(t("pwErrMismatch"));
    setBusy(true);
    try { await CL.resetPassword(params.get("token") || "", pw); setDone(true); }
    catch { setErr(t("resetFail")); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title={t("resetTitle")} subtitle={t("pwRule")} footer={<BackToLogin />}>
      {done ? (
        <div className="flex items-start gap-2 rounded-lg bg-[#eef6ef] px-4 py-3 text-sm text-[#14512f]" data-testid="reset-done">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{t("resetOk")}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3.5" data-testid="reset-form">
          {err && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700" data-testid="reset-error">{err}</div>}
          <IconInput icon={Lock} type="password" placeholder={t("pwNew")} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" autoComplete="new-password" />
          <IconInput icon={Lock} type="password" placeholder={t("pwConfirm")} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" autoComplete="new-password" />
          <button type="submit" disabled={busy || !pw || !pw2} className={btnCls} data-testid="reset-submit">{t("resetSubmit")}</button>
        </form>
      )}
    </AuthShell>
  );
}
